
import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { X, Edit3, Check, Star, ChevronDown, MessageSquare, Heart, CloudUpload, Users, AlertTriangle, Lightbulb, Target } from 'lucide-react';
import { NoteType } from '../types/notes';

// Define the proper node data type for React Flow
interface StickyNoteData extends Record<string, unknown> {
  type: NoteType;
  content: string;
  isEditing: boolean;
  highlighted?: boolean;
  onSave?: (nodeId: string, content: string) => void;
  onDelete?: (nodeId: string) => void;
  onEdit?: (nodeId: string, isEditing: boolean) => void;
  onHighlight?: (nodeId: string) => void;
  onChangeType?: (nodeId: string, newType: NoteType) => void;
}

const noteTypeLabels: Record<NoteType, string> = {
  note: 'Note',
  quote: 'Quote',
  emotion: 'Emotion',
  behaviour: 'Behaviour',
  pain: 'Pain Point',
  insight: 'Insight',
  opportunity: 'Opportunity'
};

const noteTypeColors: Record<NoteType, string> = {
  note: 'bg-note-default border-gray-300',
  quote: 'bg-note-quote border-yellow-300',
  emotion: 'bg-note-emotion border-pink-300',
  behaviour: 'bg-note-observation border-blue-300',
  pain: 'bg-note-pain border-red-300',
  insight: 'bg-note-insight border-green-300',
  opportunity: 'bg-note-insight border-purple-300'
};

const noteTypeIcons: Record<NoteType, React.ComponentType<{ className?: string }>> = {
  note: CloudUpload,
  quote: MessageSquare,
  emotion: Heart,
  behaviour: Users,
  pain: AlertTriangle,
  insight: Lightbulb,
  opportunity: Target
};

// Safe getter for StickyNoteData that preserves the note type
const getSafeNoteData = (data: Record<string, unknown>): StickyNoteData => {
  console.log('getSafeNoteData input:', data);
  
  // Ensure we preserve the note type from the data
  const noteType = (data.type as NoteType) || 'note';
  const content = (data.content as string) || '';
  const isEditing = (data.isEditing as boolean) || false;
  const highlighted = (data.highlighted as boolean) || false;
  
  console.log('getSafeNoteData output:', { noteType, content, isEditing, highlighted });
  
  return {
    type: noteType,
    content,
    isEditing,
    highlighted,
    onSave: data.onSave as ((nodeId: string, content: string) => void) | undefined,
    onDelete: data.onDelete as ((nodeId: string) => void) | undefined,
    onEdit: data.onEdit as ((nodeId: string, isEditing: boolean) => void) | undefined,
    onHighlight: data.onHighlight as ((nodeId: string) => void) | undefined,
    onChangeType: data.onChangeType as ((nodeId: string, newType: NoteType) => void) | undefined,
  };
};

export const StickyNote = memo(({ id, data }: NodeProps) => {
  const noteData = getSafeNoteData(data || {});
  const [localContent, setLocalContent] = useState(noteData.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isEditing = noteData.isEditing;
  const noteType = noteData.type;
  const isHighlighted = noteData.highlighted || false;

  console.log('StickyNote render:', { id, noteType, isEditing, content: noteData.content, highlighted: isHighlighted, hasOnSave: !!noteData.onSave });

  // Update local content when external content changes but not when editing
  useEffect(() => {
    if (!isEditing && noteData.content !== undefined) {
      console.log('Updating local content from external:', noteData.content);
      setLocalContent(noteData.content);
    }
  }, [noteData.content, isEditing]);

  // Enhanced focus management for new notes
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          // Position cursor at end of existing content
          const length = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(length, length);
          console.log('Auto-focused new note:', id);
        }
      });
    }
  }, [isEditing, id]);

  const handleSave = useCallback(() => {
    console.log('StickyNote handleSave called:', { id, localContent, hasOnSave: !!noteData.onSave });
    if (noteData.onSave && localContent.trim() !== '') {
      console.log('Calling noteData.onSave with:', { id, content: localContent.trim() });
      noteData.onSave(id, localContent.trim());
    } else {
      console.log('Save conditions not met:', { hasOnSave: !!noteData.onSave, hasContent: localContent.trim() !== '' });
    }
  }, [id, localContent, noteData]);

  const handleCancel = useCallback(() => {
    console.log('StickyNote handleCancel called:', { id, restoringContent: noteData.content });
    setLocalContent(noteData.content);
    if (noteData.onEdit) {
      noteData.onEdit(id, false);
    }
  }, [id, noteData]);

  const handleDelete = useCallback(() => {
    console.log('StickyNote handleDelete called:', id);
    if (noteData.onDelete) {
      noteData.onDelete(id);
    }
  }, [id, noteData]);

  const handleEdit = useCallback(() => {
    console.log('StickyNote handleEdit called:', id);
    if (noteData.onEdit) {
      noteData.onEdit(id, true);
    }
  }, [id, noteData]);

  const handleToggleHighlight = useCallback(() => {
    console.log('StickyNote handleToggleHighlight called:', { id, currentHighlighted: isHighlighted });
    if (noteData.onHighlight) {
      console.log('About to call onHighlight for:', id);
      noteData.onHighlight(id);
    }
  }, [id, noteData, isHighlighted]);

  const handleChangeType = useCallback((newType: NoteType) => {
    console.log('StickyNote handleChangeType called:', { id, newType });
    if (noteData.onChangeType) {
      noteData.onChangeType(id, newType);
    }
  }, [id, noteData]);

  // Auto-save on blur only if content has changed and is not empty
  const handleBlur = useCallback(() => {
    if (isEditing) {
      const trimmedContent = localContent.trim();
      if (trimmedContent !== '' && trimmedContent !== noteData.content.trim()) {
        console.log('Auto-saving on blur:', { id, content: trimmedContent });
        handleSave();
      } else if (trimmedContent === '') {
        console.log('Empty content on blur, canceling edit');
        handleCancel();
      } else {
        // Content hasn't changed, just exit edit mode
        if (noteData.onEdit) {
          noteData.onEdit(id, false);
        }
      }
    }
  }, [isEditing, localContent, noteData.content, handleSave, handleCancel, id, noteData]);

  const colorClasses = noteTypeColors[noteType];
  
  // Enhanced highlighting for search results
  const searchHighlightClasses = isHighlighted ? 
    'ring-4 ring-blue-400 ring-opacity-60 scale-105 shadow-2xl z-50' : '';
  const normalHighlightClasses = noteData.highlighted ? 'ring-2 ring-yellow-400 ring-offset-2' : '';
  
  const TypeIcon = noteTypeIcons[noteType];

  return (
    <div className={`min-w-48 max-w-72 md:min-w-64 md:max-w-80 p-3 md:p-4 rounded-lg border-2 shadow-lg transition-all duration-300 ${colorClasses} ${searchHighlightClasses || normalHighlightClasses}`}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-2 h-2 md:w-3 md:h-3 !bg-gray-400 !border-2 !border-white"
      />
      
      <div className="flex items-center justify-between mb-2 md:mb-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-auto p-1 hover:bg-white/50">
              <Badge variant="secondary" className="text-xs font-medium flex items-center gap-1">
                <TypeIcon className="w-3 h-3" />
                {noteTypeLabels[noteType]}
                <ChevronDown className="w-2.5 h-2.5" />
              </Badge>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {Object.entries(noteTypeLabels).map(([type, label]) => {
              const Icon = noteTypeIcons[type as NoteType];
              return (
                <DropdownMenuItem
                  key={type}
                  onClick={() => handleChangeType(type as NoteType)}
                  className="flex items-center gap-2"
                >
                  <Icon className="w-3 h-3" />
                  {label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex space-x-1">
          <Button
            variant="ghost"
            size="sm"
            className={`w-5 h-5 md:w-6 md:h-6 p-0 ${isHighlighted ? 'text-yellow-500 hover:bg-yellow-100' : 'hover:bg-white/50'}`}
            onClick={handleToggleHighlight}
          >
            <Star className={`w-2.5 h-2.5 md:w-3 md:h-3 ${isHighlighted ? 'fill-current' : ''}`} />
          </Button>
          {!isEditing && (
            <Button
              variant="ghost"
              size="sm"
              className="w-5 h-5 md:w-6 md:h-6 p-0 hover:bg-white/50"
              onClick={handleEdit}
            >
              <Edit3 className="w-2.5 h-2.5 md:w-3 md:h-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-5 h-5 md:w-6 md:h-6 p-0 hover:bg-red-100"
            onClick={handleDelete}
          >
            <X className="w-2.5 h-2.5 md:w-3 md:h-3" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2 md:space-y-3">
          <Textarea
            ref={textareaRef}
            value={localContent}
            onChange={(e) => setLocalContent(e.target.value)}
            onBlur={handleBlur}
            placeholder={`Enter your ${noteTypeLabels[noteType].toLowerCase()}...`}
            className="min-h-16 md:min-h-20 text-xs md:text-sm border-0 bg-white/50 resize-none focus:bg-white"
            autoFocus
          />
          <div className="flex space-x-2">
            <Button
              size="sm"
              onClick={() => {
                console.log('Save button clicked!', { id, localContent, disabled: localContent.trim() === '' });
                handleSave();
              }}
              className="flex-1 h-7 md:h-8 text-xs"
              disabled={localContent.trim() === ''}
            >
              <Check className="w-2.5 h-2.5 md:w-3 md:h-3 mr-1" />
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              className="flex-1 h-7 md:h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div 
          className="text-xs md:text-sm text-gray-700 whitespace-pre-wrap break-words cursor-pointer"
          onClick={handleEdit}
        >
          {noteData.content || (
            <span className="text-gray-400 italic">
              Click to add {noteTypeLabels[noteType].toLowerCase()}...
            </span>
          )}
        </div>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2 h-2 md:w-3 md:h-3 !bg-gray-400 !border-2 !border-white"
      />
    </div>
  );
});

StickyNote.displayName = 'StickyNote';
